import React, { useEffect } from 'react';

// chakra imports
import {
  Box,
  Flex,
  Drawer,
  DrawerBody,
  IconButton,
  useColorModeValue,
  useBreakpointValue,
  DrawerOverlay,
  useDisclosure,
  DrawerContent,
  DrawerCloseButton,
} from '@chakra-ui/react';
import Content from './components/Content';
import { renderThumb, renderTrack, renderView } from '../scrollbar/Scrollbar';
import { Scrollbars } from 'react-custom-scrollbars-2';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'i18n/TranslationProvider';

// Assets
import { IoMenuOutline } from 'react-icons/io5';

function Sidebar(props) {
  const { routes, width = 240, onNavigate, display: displayProp, ...rest } = props;

  let variantChange = '0.2s linear';
  let shadow = useColorModeValue(
    '14px 17px 40px 4px rgba(112, 144, 176, 0.08)',
    'unset'
  );
  // Chakra Color Mode
  let sidebarBg = useColorModeValue('white', 'navy.800');
  let sidebarMargins = '0px';
  const sidebarWidth = Math.max(width, 210);
  const isCompactLayout = sidebarWidth <= 260;

  // SIDEBAR
  const desktopDisplay = displayProp || { base: 'none', xl: 'block' };
  const isDesktop = useBreakpointValue({ base: false, xl: true });
  if (!isDesktop) {
    return null;
  }

  return (
    <Box
      display={desktopDisplay}
      w={`${sidebarWidth}px`}
      position="fixed"
      left="0"
      top="0"
      minH="100%"
      zIndex="2"
      borderRight="1px solid"
      borderColor="gray.100"
      {...rest}
    >
      <Box
        bg={sidebarBg}
        transition={variantChange}
        w={`${sidebarWidth}px`}
        h="100vh"
        m={sidebarMargins}
        minH="100%"
        overflowX="hidden"
        boxShadow={shadow}
        position="relative"
      >
        <Scrollbars
          autoHide
          renderTrackVertical={renderTrack}
          renderThumbVertical={renderThumb}
          renderView={renderView}
        >
          <Content
            routes={routes}
            isCompact={isCompactLayout}
            isCollapsed={false}
            onLinkClick={onNavigate}
          />
        </Scrollbars>
      </Box>
    </Box>
  );
}

// FUNCTIONS
export function SidebarResponsive(props) {
  let sidebarBackgroundColor = useColorModeValue('white', 'navy.800');
  let menuColor = useColorModeValue('gray.400', 'white');
  const btnRef = React.useRef();

  const {
    routes,
    width = 240,
    onNavigate,
    display: displayProp,
    isOpen: controlledIsOpen,
    onOpen: controlledOnOpen,
    onClose: controlledOnClose,
    showTrigger = true,
    setTheme,
    ...rest
  } = props;
  const internalDisclosure = useDisclosure();
  const isControlled =
    typeof controlledIsOpen === 'boolean' &&
    typeof controlledOnClose === 'function' &&
    typeof controlledOnOpen === 'function';
  const isOpen = isControlled ? controlledIsOpen : internalDisclosure.isOpen;
  const onOpen = isControlled ? controlledOnOpen : internalDisclosure.onOpen;
  const onClose = isControlled ? controlledOnClose : internalDisclosure.onClose;
  const drawerWidth = Math.min(Math.max(width, 260), 320);
  const location = useLocation();
  const isMobile = useBreakpointValue({ base: true, xl: false });
  const { t } = useTranslation();

  useEffect(() => {
    onClose();
  }, [location.pathname, onClose]);

  // let isWindows = navigator.platform.startsWith("Win");
  //  BRAND

  const responsiveDisplay = displayProp || { base: 'flex', xl: 'none' };

  if (!isMobile) {
    return null;
  }

  return (
    <Flex display={responsiveDisplay} alignItems="center" {...rest}>
      {showTrigger && (
        <IconButton
          ref={btnRef}
          onClick={onOpen}
          aria-label={t('Open navigation menu')}
          icon={<IoMenuOutline size="24px" />}
          color={menuColor}
          bg="white"
          borderRadius="full"
          boxShadow="lg"
          _hover={{ bg: 'gray.50', transform: 'translateY(-1px)' }}
          _active={{ bg: 'gray.100' }}
          size="lg"
        />
      )}
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        placement={document.documentElement.dir === 'rtl' ? 'right' : 'left'}
        finalFocusRef={showTrigger ? btnRef : undefined}
        size="xs"
      >
        <DrawerOverlay />
        <DrawerContent
          w="90vw"
          maxW={`${drawerWidth}px`}
          bg={sidebarBackgroundColor}
          borderRightRadius="24px"
        >
          <DrawerCloseButton
            zIndex="3"
            onClose={onClose}
            _focus={{ boxShadow: 'none' }}
            _hover={{ boxShadow: 'none' }}
          />
          <DrawerBody maxW="100%" px="0" pb="0">
            <Scrollbars
              autoHide
              renderTrackVertical={renderTrack}
              renderThumbVertical={renderThumb}
              renderView={renderView}
            >
              <Content
                routes={routes}
                isCompact
                isCollapsed={false}
                onLinkClick={() => {
                  if (typeof onNavigate === 'function') onNavigate();
                  onClose();
                }}
              />
            </Scrollbars>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Flex>
  );
}
// PROPS

Sidebar.propTypes = {
  logoText: PropTypes.string,
  routes: PropTypes.arrayOf(PropTypes.object),
  variant: PropTypes.string,
  width: PropTypes.number,
  onNavigate: PropTypes.func,
};

SidebarResponsive.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object),
  width: PropTypes.number,
  onNavigate: PropTypes.func,
  display: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  isOpen: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  showTrigger: PropTypes.bool,
};

export default Sidebar;
